import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, MessageCircle, ShieldCheck, MapPin } from "lucide-react";
import { WHATSAPP_URL } from "@/lib/constants";

const HERO_BG =
  "https://images.unsplash.com/photo-1542705959-878ca346eb20?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

// Masked line reveal variants
const lineParent = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.18, delayChildren: 0.3 },
  },
};
const lineChild = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden isolate noise-overlay"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 z-0 will-change-transform"
      >
        <img
          src={HERO_BG}
          alt="Camion in autostrada di notte"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#050505]" />
        <div className="absolute -bottom-32 -right-32 h-[500px] w-[500px] rounded-full bg-[#0055ff] opacity-25 blur-[140px] animate-float-slow" />
        <div className="absolute top-1/3 -left-32 h-[420px] w-[420px] rounded-full bg-[#00e5ff] opacity-10 blur-[160px]" />
      </motion.div>

      {/* Grid overlay - subtle tech vibe */}
      <div
        className="absolute inset-0 z-[1] opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      {/* Side label */}
      <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 origin-left -rotate-90 items-center gap-3 text-xs uppercase tracking-[0.5em] text-blue-300/70">
        <span className="h-px w-10 bg-blue-400/50" />
        Canicattì · Sicilia · Italia
      </div>

      {/* Vertical chapter counter */}
      <div className="hidden xl:block absolute right-8 top-1/2 -translate-y-1/2 z-10 text-right">
        <div className="font-heading text-[10px] uppercase tracking-[0.35em] text-blue-300/60 mb-2">
          Capitolo
        </div>
        <div className="font-heading text-6xl font-black metal-text-gradient leading-none">
          00
        </div>
        <div className="mt-2 font-heading text-[10px] uppercase tracking-[0.35em] text-white/50">
          Intro
        </div>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 w-full will-change-transform"
      >
        {/* Tag */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="inline-flex items-center gap-2 px-4 py-2 mb-8 border border-blue-500/30 bg-blue-500/5 backdrop-blur-sm"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-blue-200">
            Trasporti & Logistica Premium
          </span>
        </motion.div>

        {/* Masked line reveal title */}
        <motion.h1
          data-testid="hero-title"
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-[92px] font-black tracking-[-0.03em] leading-[0.92] uppercase max-w-5xl"
          aria-label="BENNICI AUTOTRASPORTI CANICATTÌ"
        >
          <span className="block overflow-hidden">
            <motion.span variants={lineChild} className="block text-white">
              Bennici
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              variants={lineChild}
              className="block metal-text-gradient"
            >
              Autotrasporti
            </motion.span>
          </span>
        </motion.h1>

        {/* Location badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 inline-flex items-center gap-3 text-[11px] font-heading uppercase tracking-[0.4em] text-blue-200"
        >
          <MapPin size={14} className="text-blue-400" />
          Canicattì · Sicilia
        </motion.div>

        {/* Slogan tagline underline */}
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "88px" }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-6 h-[2px] bg-gradient-to-r from-[#00e5ff] to-transparent"
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15 }}
          className="mt-6 max-w-2xl text-lg md:text-xl text-gray-300 leading-relaxed font-light"
          data-testid="hero-subtitle"
        >
          Soluzioni professionali di{" "}
          <span className="text-white font-medium">trasporto e logistica</span>{" "}
          per aziende e privati. Ogni viaggio viene seguito con attenzione,
          garantendo{" "}
          <span className="text-blue-300">
            sicurezza, puntualità e massima cura della merce.
          </span>
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="shimmer-btn group relative inline-flex items-center gap-3 px-8 md:px-10 py-5 bg-[#0055ff] text-white font-heading font-bold uppercase tracking-[0.15em] text-sm md:text-base animate-pulse-glow hover:bg-[#0066ff] transition-colors"
          >
            <MessageCircle size={20} strokeWidth={2.5} />
            <span>Richiedi Preventivo</span>
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>

          <a
            href="#servizi"
            data-testid="hero-cta-services"
            className="inline-flex items-center gap-2 px-7 py-5 text-sm font-bold uppercase tracking-[0.15em] text-white/90 hover:text-white border border-white/15 hover:border-blue-400/60 transition-colors"
          >
            Scopri i servizi
            <ArrowRight size={16} />
          </a>
        </motion.div>

        {/* Quick stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.55 }}
          className="mt-16 grid grid-cols-3 gap-4 md:gap-10 max-w-2xl"
        >
          {[
            { icon: MapPin, value: "Italia", label: "Copertura nazionale" },
            { icon: ShieldCheck, value: "100%", label: "Merce protetta" },
            { icon: MessageCircle, value: "24/7", label: "Assistenza diretta" },
          ].map((s, i) => (
            <div
              key={i}
              className="flex flex-col gap-2 border-l border-blue-500/30 pl-4"
            >
              <s.icon size={18} className="text-blue-400" />
              <div className="font-heading text-2xl md:text-3xl font-black text-white">
                {s.value}
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-blue-300/70"
      >
        <span className="text-[10px] uppercase tracking-[0.35em]">Scroll</span>
        <motion.span
          animate={{ scaleY: [0.4, 1, 0.4], originY: 0 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block h-10 w-px bg-gradient-to-b from-blue-400 to-transparent"
        />
      </motion.div>
    </section>
  );
}
